import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import GoogleFontPicker from './GoogleFontPicker';
import { apiClient, API_ENDPOINTS, useAuthStore } from '@dukene/shared';
import {
  Storefront,
  Palette,
  UploadSimple,
  Image as ImageIcon,
  Globe,
  MagnifyingGlass,
  ArrowSquareOut,
  CheckCircle,
  WarningCircle,
  SlidersHorizontal,
  PaintBrush,
  ShareNetwork,
  FloppyDisk,
  X,
} from '@phosphor-icons/react';
import {
  FacebookLogo,
  InstagramLogo,
  TikTokLogo,
  YouTubeLogo,
  XLogo,
  LinkedInLogo,
} from '../../components/icons/BrandIcons';

// Store branding settings schema
const storeBrandingSchema = z.object({
  name: z.string().min(2, 'Store name must be at least 2 characters'),
  description: z.string().optional(),
  primaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color'),
  secondaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color'),
  accentColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color'),
  bannerImage: z.string().url().optional().or(z.literal('')),
  favicon: z.string().url().optional().or(z.literal('')),
  logo: z.string().url().optional().or(z.literal('')),
  customDomain: z.string().optional().or(z.literal('')),
  facebook: z.string().url().optional().or(z.literal('')),
  twitter: z.string().url().optional().or(z.literal('')),
  instagram: z.string().url().optional().or(z.literal('')),
  tiktok: z.string().url().optional().or(z.literal('')),
  youtube: z.string().url().optional().or(z.literal('')),
  linkedin: z.string().url().optional().or(z.literal('')),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  metaKeywords: z.string().optional(),
  fbPixelId: z.string().optional(),
  tiktokPixelId: z.string().optional(),
  gtmContainerId: z.string().optional(),
  fontHeading: z.string().optional(),
  fontBody: z.string().optional(),
  fontNav: z.string().optional(),
  fontLogo: z.string().optional(),
  logoAppearance: z.enum(['logo-only', 'logo-and-name', 'name-only']).optional(),
  enableReviews: z.boolean().optional(),
  enableWishlist: z.boolean().optional(),
  enableChat: z.boolean().optional(),
  footerText: z.string().optional(),
  newsletterTitle: z.string().optional(),
  newsletterDescription: z.string().optional(),
  themeId: z.string().optional(),
  displayEmail: z.string().email('Must be a valid email').optional().or(z.literal('')),
  displayPhone: z.string().optional().or(z.literal('')),
});

type StoreBrandingFormData = z.infer<typeof storeBrandingSchema>;

interface StoreSettingsData {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  bannerImage?: string;
  favicon?: string;
  fontHeading?: string;
  fontBody?: string;
  fontNav?: string;
  fontLogo?: string;
  logoAppearance?: 'logo-only' | 'logo-and-name' | 'name-only';
  facebook?: string;
  twitter?: string;
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  linkedin?: string;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  fbPixelId?: string;
  tiktokPixelId?: string;
  gtmContainerId?: string;
  footerText?: string;
  enableReviews?: boolean;
  enableWishlist?: boolean;
  enableChat?: boolean;
  newsletterTitle?: string;
  newsletterDescription?: string;
  themeId?: string;
  displayEmail?: string;
  displayPhone?: string;
}

interface StoreData {
  id: string;
  name: string;
  subdomain: string;
  customDomain?: string;
  description?: string;
  logo?: string;
  status?: string;
  settings?: StoreSettingsData;
}

type StoreTab = 'general' | 'design' | 'domains' | 'seo' | 'features';

export default function StoreSettings() {
  const queryClient = useQueryClient();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<StoreTab>(() => {
    const path = location.pathname;
    if (path.includes('/design')) return 'design';
    if (path.includes('/domains')) return 'domains';
    if (path.includes('/seo')) return 'seo';
    if (path.includes('/features')) return 'features';
    return 'general';
  });

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/design') && activeTab !== 'design') setActiveTab('design');
    else if (path.includes('/domains') && activeTab !== 'domains') setActiveTab('domains');
    else if (path.includes('/seo') && activeTab !== 'seo') setActiveTab('seo');
    else if (path.includes('/features') && activeTab !== 'features') setActiveTab('features');
    else if (path === '/store' && activeTab !== 'general') setActiveTab('general');
  }, [location.pathname, activeTab]);

  const handleTabChange = (tab: StoreTab) => {
    setActiveTab(tab);
    if (tab === 'general') navigate('/store');
    else navigate(`/store/${tab}`);
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  const {
    data: store,
    isLoading,
    error,
  } = useQuery<StoreData>({
    queryKey: ['store-settings', activeStoreId],
    queryFn: async () => {
      return apiClient.get<StoreData>(API_ENDPOINTS.MERCHANT.STORE);
    },
    enabled: !!activeStoreId,
  });

  const getFontName = (stack: string | undefined, fallback: string) => {
    if (!stack) return fallback;
    return stack.split(',')[0].replace(/['"]/g, '').trim();
  };

  const { register, handleSubmit, setValue, watch, reset } = useForm<StoreBrandingFormData>({
    resolver: zodResolver(storeBrandingSchema),
    defaultValues: {
      name: '',
      description: '',
      primaryColor: '#006838',
      secondaryColor: '#FFD700',
      accentColor: '#FF6B35',
      bannerImage: '',
      favicon: '',
      logo: '',
      customDomain: '',
      facebook: '',
      twitter: '',
      instagram: '',
      tiktok: '',
      youtube: '',
      linkedin: '',
      metaTitle: '',
      metaDescription: '',
      metaKeywords: '',
      fontHeading: 'Playfair Display',
      fontBody: 'Poppins',
      fontNav: 'Satisfy',
      fontLogo: 'Questrial',
      logoAppearance: 'logo-and-name',
      enableReviews: true,
      enableWishlist: true,
      enableChat: false,
      footerText: '',
      newsletterTitle: '',
      newsletterDescription: '',
      themeId: 'default',
      displayEmail: '',
      displayPhone: '',
    },
  });

  useEffect(() => {
    if (store) {
      reset({
        name: store.name || '',
        description: store.description || '',
        primaryColor: store.settings?.primaryColor || '#006838',
        secondaryColor: store.settings?.secondaryColor || '#FFD700',
        accentColor: store.settings?.accentColor || '#FF6B35',
        bannerImage: store.settings?.bannerImage || '',
        favicon: store.settings?.favicon || '',
        logo: store.logo || '',
        customDomain: store.customDomain || '',
        facebook: store.settings?.facebook || '',
        twitter: store.settings?.twitter || '',
        instagram: store.settings?.instagram || '',
        tiktok: store.settings?.tiktok || '',
        youtube: store.settings?.youtube || '',
        linkedin: store.settings?.linkedin || '',
        metaTitle: store.settings?.metaTitle || '',
        metaDescription: store.settings?.metaDescription || '',
        metaKeywords: store.settings?.metaKeywords || '',
        fontHeading: getFontName(store.settings?.fontHeading, 'Playfair Display'),
        fontBody: getFontName(store.settings?.fontBody, 'Poppins'),
        fontNav: getFontName(store.settings?.fontNav, 'Satisfy'),
        fontLogo: getFontName(store.settings?.fontLogo, 'Questrial'),
        logoAppearance: store.settings?.logoAppearance || 'logo-and-name',
        enableReviews: store.settings?.enableReviews ?? true,
        enableWishlist: store.settings?.enableWishlist ?? true,
        enableChat: store.settings?.enableChat ?? false,
        footerText: store.settings?.footerText || '',
        newsletterTitle: store.settings?.newsletterTitle || '',
        newsletterDescription: store.settings?.newsletterDescription || '',
        themeId: store.settings?.themeId || 'default',
        displayEmail: store.settings?.displayEmail || '',
        displayPhone: store.settings?.displayPhone || '',
      });
      setLogoPreview(store.logo || null);
      setBannerPreview(store.settings?.bannerImage || null);
      setFaviconPreview(store.settings?.favicon || null);
    }
  }, [store, reset]);

  const uploadImage = async (file: File, folder: string): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    const response = await apiClient.getClient().post('/uploads/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data.url;
  };

  const handleImageUpload = async (file: File, type: 'logo' | 'banner' | 'favicon') => {
    if (!file) return;
    const setters = {
      logo: { preview: setLogoPreview, loading: setUploadingLogo, field: 'logo' },
      banner: { preview: setBannerPreview, loading: setUploadingBanner, field: 'bannerImage' },
      favicon: { preview: setFaviconPreview, loading: setUploadingFavicon, field: 'favicon' },
    };

    const config = setters[type];
    config.preview(URL.createObjectURL(file));
    config.loading(true);

    try {
      const url = await uploadImage(file, `store-${type}s`);
      setValue(config.field as any, url, { shouldDirty: true });
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Failed to upload image. Please try again.');
    } finally {
      config.loading(false);
    }
  };

  const updateStoreMutation = useMutation({
    mutationFn: async (data: StoreBrandingFormData) => {
      const payload = {
        name: data.name,
        description: data.description,
        logo: data.logo,
        customDomain: data.customDomain,
        settings: {
          primaryColor: data.primaryColor,
          secondaryColor: data.secondaryColor,
          accentColor: data.accentColor,
          bannerImage: data.bannerImage,
          favicon: data.favicon,
          facebook: data.facebook,
          twitter: data.twitter,
          instagram: data.instagram,
          tiktok: data.tiktok,
          youtube: data.youtube,
          linkedin: data.linkedin,
          metaTitle: data.metaTitle,
          metaDescription: data.metaDescription,
          metaKeywords: data.metaKeywords,
          fontHeading: data.fontHeading,
          fontBody: data.fontBody,
          fontNav: data.fontNav,
          fontLogo: data.fontLogo,
          logoAppearance: data.logoAppearance,
          enableReviews: data.enableReviews,
          enableWishlist: data.enableWishlist,
          enableChat: data.enableChat,
          footerText: data.footerText,
          newsletterTitle: data.newsletterTitle,
          newsletterDescription: data.newsletterDescription,
          themeId: data.themeId,
          displayEmail: data.displayEmail,
          displayPhone: data.displayPhone,
        },
      };
      return apiClient.patch(API_ENDPOINTS.MERCHANT.STORE, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-settings', activeStoreId] });
      queryClient.invalidateQueries({ queryKey: ['merchant-store', activeStoreId] });
      queryClient.invalidateQueries({ queryKey: ['merchant-store'] });
      queryClient.invalidateQueries({ queryKey: ['merchant-channels', activeStoreId] });
      setIsEditMode(false);
    },
    onError: (error: any) => {
      alert(`Save failed: ${error?.response?.data?.message || error.message}`);
    },
  });

  const togglePublishMutation = useMutation({
    mutationFn: async () => {
      const nextStatus = store?.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      return apiClient.patch(API_ENDPOINTS.MERCHANT.STORE, { status: nextStatus });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-settings', activeStoreId] });
      queryClient.invalidateQueries({ queryKey: ['merchant-store', activeStoreId] });
      queryClient.invalidateQueries({ queryKey: ['merchant-store'] });
      queryClient.invalidateQueries({ queryKey: ['merchant-channels', activeStoreId] });
    },
    onError: (error: any) => {
      alert(`Failed to update store status: ${error?.response?.data?.message || error.message}`);
    },
  });

  const onSubmit = (data: StoreBrandingFormData) => {
    updateStoreMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#006838] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-600 bg-red-50 border border-red-200 rounded-lg">
        Failed to load online store settings.
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-16">
      {/* High-density Polaris Admin Header & Tab Bar */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl pt-5 px-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E1E4E8] dark:bg-slate-800 flex items-center justify-center text-[#202223] dark:text-slate-200">
              <Storefront weight="fill" className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-semibold text-[#202223] dark:text-white">Online Store Design & Preferences</h1>
                {store?.status === 'ACTIVE' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-[#E3F1DF] text-[#006838] border border-[#B3DEC1]">
                    <CheckCircle weight="fill" className="w-3 h-3" /> Storefront Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-[#FFF4E5] text-[#B95000] border border-[#FFD79E]">
                    <WarningCircle weight="fill" className="w-3 h-3" /> Reserved / Offline
                  </span>
                )}
              </div>
              <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                Configure your storefront identity, theme typography, custom domain, SEO, and social presence
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {store?.status === 'ACTIVE' && (
              <a
                href={`https://${store?.subdomain || 'shop'}.dukene.com`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-[#202223] dark:text-slate-200 hover:bg-[#F6F6F7] dark:hover:bg-slate-700 transition-colors"
              >
                <ArrowSquareOut weight="bold" className="w-3.5 h-3.5" />
                View Storefront
              </a>
            )}
            <button
              type="button"
              onClick={() => togglePublishMutation.mutate()}
              disabled={togglePublishMutation.isPending}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold rounded-md transition-all shadow-sm ${
                store?.status === 'ACTIVE'
                  ? 'bg-white dark:bg-slate-800 border border-[#D2D5D9] dark:border-slate-700 text-[#D82C0D] hover:bg-red-50 dark:hover:bg-red-950/30'
                  : 'bg-[#006838] hover:bg-[#00522c] text-white'
              }`}
            >
              {togglePublishMutation.isPending && (
                <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              )}
              {store?.status === 'ACTIVE' ? 'Unpublish Store' : 'Publish Store Now'}
            </button>
            {!isEditMode ? (
              <button
                type="button"
                onClick={() => setIsEditMode(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium rounded-md border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-[#202223] dark:text-slate-200 hover:bg-[#F6F6F7] transition-colors"
              >
                <Palette weight="bold" className="w-3.5 h-3.5" />
                Customize Style
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIsEditMode(false)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-[#D2D5D9] bg-white text-[#5C5F62] hover:bg-[#F6F6F7]"
              >
                <X weight="bold" className="w-3.5 h-3.5" />
                Done
              </button>
            )}
          </div>
        </div>

        {/* Prominent Store Status & Publish Callout Box */}
        <div className={`mt-2 mb-5 p-4 rounded-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 transition-all shadow-sm ${
          store?.status === 'ACTIVE'
            ? 'bg-[#E3F1DF]/60 dark:bg-emerald-950/20 border-[#B3DEC1] text-[#006838] dark:text-emerald-400'
            : 'bg-[#FFF4E5] dark:bg-amber-950/20 border-[#FFD79E] text-[#B95000] dark:text-amber-400'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
              store?.status === 'ACTIVE' ? 'bg-[#006838] text-white' : 'bg-[#B95000] text-white'
            }`}>
              {store?.status === 'ACTIVE' ? <CheckCircle weight="fill" className="w-5 h-5" /> : <WarningCircle weight="fill" className="w-5 h-5" />}
            </div>
            <div>
              <h3 className="text-sm font-bold tracking-tight">
                {store?.status === 'ACTIVE' ? 'Your Online Store is Live and Accessible' : 'Your Online Store is Offline (Subdomain Reserved)'}
              </h3>
              <p className="text-xs mt-0.5 opacity-90">
                {store?.status === 'ACTIVE'
                  ? `Shoppers around the world can visit your store at ${store?.subdomain || 'shop'}.dukene.com.`
                  : `Your subdomain (${store?.subdomain || 'shop'}.dukene.com) is reserved exclusively for you. Publish your store when your catalog and design are ready.`}
              </p>
            </div>
          </div>
        </div>

        {/* Polaris Navigation Tabs */}
        <div className="flex items-center gap-6 border-t border-[#E1E4E8] dark:border-slate-800 -mx-5 px-5">
          <button
            type="button"
            onClick={() => handleTabChange('general')}
            className={`flex items-center gap-2 py-3 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'general'
                ? 'border-[#006838] text-[#006838] dark:text-emerald-400 font-semibold'
                : 'border-transparent text-[#5C5F62] dark:text-slate-400 hover:text-[#202223] dark:hover:text-slate-200'
            }`}
          >
            <Storefront weight="fill" className="w-4 h-4" />
            General Identity
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('design')}
            className={`flex items-center gap-2 py-3 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'design'
                ? 'border-[#006838] text-[#006838] dark:text-emerald-400 font-semibold'
                : 'border-transparent text-[#5C5F62] dark:text-slate-400 hover:text-[#202223] dark:hover:text-slate-200'
            }`}
          >
            <PaintBrush weight="fill" className="w-4 h-4" />
            Theme & Typography
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('domains')}
            className={`flex items-center gap-2 py-3 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'domains'
                ? 'border-[#006838] text-[#006838] dark:text-emerald-400 font-semibold'
                : 'border-transparent text-[#5C5F62] dark:text-slate-400 hover:text-[#202223] dark:hover:text-slate-200'
            }`}
          >
            <Globe weight="fill" className="w-4 h-4" />
            Domains & URLs
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('seo')}
            className={`flex items-center gap-2 py-3 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'seo'
                ? 'border-[#006838] text-[#006838] dark:text-emerald-400 font-semibold'
                : 'border-transparent text-[#5C5F62] dark:text-slate-400 hover:text-[#202223] dark:hover:text-slate-200'
            }`}
          >
            <ShareNetwork weight="fill" className="w-4 h-4" />
            SEO & Social Media
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('features')}
            className={`flex items-center gap-2 py-3 text-xs font-medium border-b-2 transition-colors ${
              activeTab === 'features'
                ? 'border-[#006838] text-[#006838] dark:text-emerald-400 font-semibold'
                : 'border-transparent text-[#5C5F62] dark:text-slate-400 hover:text-[#202223] dark:hover:text-slate-200'
            }`}
          >
            <SlidersHorizontal weight="fill" className="w-4 h-4" />
            Storefront Features
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Sticky Action Banner when Editing */}
        {isEditMode && (
          <div className="sticky top-3 z-30 flex items-center justify-between px-4 py-3 rounded-lg bg-[#202223] text-white shadow-md border border-slate-700">
            <div className="flex items-center gap-2.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs font-medium">You are customizing live storefront branding settings.</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsEditMode(false)}
                className="px-3 py-1.5 text-xs font-medium rounded text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
              >
                Discard
              </button>
              <button
                type="submit"
                disabled={updateStoreMutation.isPending}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded bg-[#006838] hover:bg-[#00522c] text-white transition-colors disabled:opacity-50"
              >
                <FloppyDisk weight="bold" className="w-3.5 h-3.5" />
                {updateStoreMutation.isPending ? 'Publishing...' : 'Save & Publish'}
              </button>
            </div>
          </div>
        )}

        {/* TAB 1: GENERAL IDENTITY */}
        {activeTab === 'general' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 space-y-4">
                <div className="border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
                  <h3 className="text-sm font-semibold text-[#202223] dark:text-white">Store Identity Information</h3>
                  <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                    Primary name, description, and footer contact credentials displayed to customers
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-[#202223] dark:text-slate-300 mb-1">
                      Store Name
                    </label>
                    <input
                      {...register('name')}
                      disabled={!isEditMode}
                      className="w-full h-9 px-3 rounded-md border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838] disabled:bg-[#F6F6F7] dark:disabled:bg-slate-900"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#202223] dark:text-slate-300 mb-1">
                      Store Description
                    </label>
                    <textarea
                      {...register('description')}
                      disabled={!isEditMode}
                      rows={3}
                      placeholder="Brief overview of your store, brand values, and catalog offerings..."
                      className="w-full p-3 rounded-md border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838] disabled:bg-[#F6F6F7] dark:disabled:bg-slate-900 resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-[#202223] dark:text-slate-300 mb-1">
                        Display Email (Storefront Footer)
                      </label>
                      <input
                        {...register('displayEmail')}
                        disabled={!isEditMode}
                        placeholder="support@yourbrand.com"
                        className="w-full h-9 px-3 rounded-md border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838] disabled:bg-[#F6F6F7]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#202223] dark:text-slate-300 mb-1">
                        Display Contact Phone
                      </label>
                      <input
                        {...register('displayPhone')}
                        disabled={!isEditMode}
                        placeholder="+1 (800) 555-0199"
                        className="w-full h-9 px-3 rounded-md border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838] disabled:bg-[#F6F6F7]"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Visual Assets Card */}
              <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 space-y-4">
                <div className="border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
                  <h3 className="text-sm font-semibold text-[#202223] dark:text-white">Brand Assets & Logos</h3>
                  <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                    Upload crisp vector or PNG artwork for your header logo, favicon icon, and promotional shop banner
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#202223] dark:text-slate-300 mb-1.5">
                      Brand Logo
                    </label>
                    <div
                      onClick={() => isEditMode && logoInputRef.current?.click()}
                      className={`w-full h-32 rounded-lg border border-dashed transition-all flex flex-col items-center justify-center p-4 bg-[#F6F6F7] dark:bg-slate-900 ${
                        isEditMode
                          ? 'hover:bg-[#E3F1DF]/30 hover:border-[#006838] cursor-pointer border-[#D2D5D9]'
                          : 'border-[#E1E4E8]'
                      }`}
                    >
                      {logoPreview ? (
                        <img src={logoPreview} alt="Brand Logo" className="max-h-20 object-contain rounded" />
                      ) : (
                        <div className="text-center space-y-1">
                          <UploadSimple weight="bold" className="w-6 h-6 text-[#5C5F62] mx-auto" />
                          <p className="text-xs font-medium text-[#5C5F62]">Click to upload brand logo</p>
                          <p className="text-[10px] text-gray-400">PNG, SVG, or JPG up to 5MB</p>
                        </div>
                      )}
                      {uploadingLogo && (
                        <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
                          <div className="w-5 h-5 border-2 border-[#006838] border-t-transparent animate-spin rounded-full" />
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      ref={logoInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files![0], 'logo')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#202223] dark:text-slate-300 mb-1.5">
                      Favicon Icon (Browser Tab)
                    </label>
                    <div
                      onClick={() => isEditMode && faviconInputRef.current?.click()}
                      className={`w-full h-32 rounded-lg border border-dashed transition-all flex flex-col items-center justify-center p-4 bg-[#F6F6F7] dark:bg-slate-900 ${
                        isEditMode
                          ? 'hover:bg-[#E3F1DF]/30 hover:border-[#006838] cursor-pointer border-[#D2D5D9]'
                          : 'border-[#E1E4E8]'
                      }`}
                    >
                      {faviconPreview ? (
                        <img src={faviconPreview} alt="Favicon" className="w-10 h-10 object-contain" />
                      ) : (
                        <div className="text-center space-y-1">
                          <Globe weight="bold" className="w-6 h-6 text-[#5C5F62] mx-auto" />
                          <p className="text-xs font-medium text-[#5C5F62]">Upload favicon icon</p>
                          <p className="text-[10px] text-gray-400">Square 32x32 or 64x64 PNG/ICO</p>
                        </div>
                      )}
                      {uploadingFavicon && (
                        <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
                          <div className="w-5 h-5 border-2 border-[#006838] border-t-transparent animate-spin rounded-full" />
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      ref={faviconInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files![0], 'favicon')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#202223] dark:text-slate-300 mb-1.5">
                    Storefront Hero Banner Image
                  </label>
                  <div
                    onClick={() => isEditMode && bannerInputRef.current?.click()}
                    className={`w-full h-36 rounded-lg border border-dashed transition-all flex flex-col items-center justify-center overflow-hidden bg-[#F6F6F7] dark:bg-slate-900 ${
                      isEditMode
                        ? 'hover:bg-[#E3F1DF]/30 hover:border-[#006838] cursor-pointer border-[#D2D5D9]'
                        : 'border-[#E1E4E8]'
                    }`}
                  >
                    {bannerPreview ? (
                      <img src={bannerPreview} alt="Hero Banner" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center space-y-1">
                        <ImageIcon weight="bold" className="w-7 h-7 text-[#5C5F62] mx-auto" />
                        <p className="text-xs font-medium text-[#5C5F62]">Upload hero banner background</p>
                        <p className="text-[10px] text-gray-400">Recommended 1920x480 wide aspect ratio</p>
                      </div>
                    )}
                    {uploadingBanner && (
                      <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-[#006838] border-t-transparent animate-spin rounded-full" />
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={bannerInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e.target.files![0], 'banner')}
                  />
                </div>
              </div>
            </div>

            {/* Right column sidebar inside General */}
            <div className="space-y-4">
              <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 space-y-4">
                <div className="border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
                  <h3 className="text-sm font-semibold text-[#202223] dark:text-white">Header Logo Layout</h3>
                  <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                    Control navigation bar brand lockup
                  </p>
                </div>
                <div className="space-y-2">
                  {[
                    { id: 'logo-and-name', label: 'Logo and Brand Name' },
                    { id: 'logo-only', label: 'Logo Only' },
                    { id: 'name-only', label: 'Brand Name Only' },
                  ].map((opt) => (
                    <button
                      type="button"
                      key={opt.id}
                      onClick={() => isEditMode && setValue('logoAppearance', opt.id as any, { shouldDirty: true })}
                      className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg border text-xs font-medium transition-all ${
                        watch('logoAppearance') === opt.id
                          ? 'border-[#006838] bg-[#E3F1DF]/40 text-[#006838] dark:text-emerald-300'
                          : 'border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-[#202223] dark:text-slate-300'
                      }`}
                    >
                      <span>{opt.label}</span>
                      <span
                        className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ${
                          watch('logoAppearance') === opt.id
                            ? 'border-[#006838] bg-[#006838]'
                            : 'border-[#8C9196]'
                        }`}
                      >
                        {watch('logoAppearance') === opt.id && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: THEME & TYPOGRAPHY DESIGN */}
        {activeTab === 'design' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Color Palette Card */}
            <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 space-y-4">
              <div className="border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
                <h3 className="text-sm font-semibold text-[#202223] dark:text-white">Brand Color Palette</h3>
                <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                  Select primary, secondary, and highlight accent colors used across buttons and banners
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg border border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7] dark:bg-slate-900">
                  <div>
                    <p className="text-xs font-semibold text-[#202223] dark:text-white">Primary Brand Color</p>
                    <p className="text-[11px] text-[#5C5F62]">{watch('primaryColor')}</p>
                  </div>
                  <input
                    type="color"
                    {...register('primaryColor')}
                    disabled={!isEditMode}
                    className="w-10 h-10 rounded-md border border-[#D2D5D9] cursor-pointer p-0 bg-transparent"
                  />
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7] dark:bg-slate-900">
                  <div>
                    <p className="text-xs font-semibold text-[#202223] dark:text-white">Secondary Color</p>
                    <p className="text-[11px] text-[#5C5F62]">{watch('secondaryColor')}</p>
                  </div>
                  <input
                    type="color"
                    {...register('secondaryColor')}
                    disabled={!isEditMode}
                    className="w-10 h-10 rounded-md border border-[#D2D5D9] cursor-pointer p-0 bg-transparent"
                  />
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg border border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7] dark:bg-slate-900">
                  <div>
                    <p className="text-xs font-semibold text-[#202223] dark:text-white">Accent Highlight Color</p>
                    <p className="text-[11px] text-[#5C5F62]">{watch('accentColor')}</p>
                  </div>
                  <input
                    type="color"
                    {...register('accentColor')}
                    disabled={!isEditMode}
                    className="w-10 h-10 rounded-md border border-[#D2D5D9] cursor-pointer p-0 bg-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Typography Selection Card */}
            <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 space-y-4">
              <div className="border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
                <h3 className="text-sm font-semibold text-[#202223] dark:text-white">Storefront Typography</h3>
                <p className="text-xs text-[#5C5F62] dark:text-slate-400">Browse Google Fonts and see each typeface before choosing it for your store.</p>
              </div>
              <input type="hidden" {...register('fontHeading')} />
              <input type="hidden" {...register('fontBody')} />
              <input type="hidden" {...register('fontLogo')} />
              <input type="hidden" {...register('fontNav')} />
              <div className="grid gap-4 sm:grid-cols-2">
                <GoogleFontPicker label="Heading font" value={watch('fontHeading') || ''} sample="Your next favorite find" disabled={!isEditMode} onChange={(font) => setValue('fontHeading', font, { shouldDirty: true })} />
                <GoogleFontPicker label="Body font" value={watch('fontBody') || ''} sample="Beautiful things for everyday living." disabled={!isEditMode} onChange={(font) => setValue('fontBody', font, { shouldDirty: true })} />
                <GoogleFontPicker label="Logo font" value={watch('fontLogo') || ''} sample={watch('name') || 'Your Store'} disabled={!isEditMode} onChange={(font) => setValue('fontLogo', font, { shouldDirty: true })} />
                <GoogleFontPicker label="Navigation font" value={watch('fontNav') || ''} sample="Shop  ·  Collections  ·  About" disabled={!isEditMode} onChange={(font) => setValue('fontNav', font, { shouldDirty: true })} />
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: DOMAINS & URLs */}
        {activeTab === 'domains' && (
          <div className="max-w-3xl space-y-4">
            <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 space-y-4">
              <div className="border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
                <h3 className="text-sm font-semibold text-[#202223] dark:text-white">Storefront Domains</h3>
                <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                  Manage system subdomain URL and connect your custom primary domain
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-3.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-[#F6F6F7] dark:bg-slate-900">
                  <div>
                    <span className="text-[10px] uppercase font-semibold text-[#5C5F62] tracking-wider block">
                      Default System Subdomain
                    </span>
                    <span className="text-xs font-semibold text-[#006838] dark:text-emerald-400">
                      https://{store?.subdomain || 'shop'}.dukene.com
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border ${
                      store?.status === 'ACTIVE'
                        ? 'bg-[#E3F1DF] text-[#006838] border-[#B3DEC1]'
                        : 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300'
                    }`}>
                      {store?.status === 'ACTIVE' ? 'Active (Live)' : 'Reserved (Offline)'}
                    </span>
                    {store?.status !== 'ACTIVE' && (
                      <button
                        type="button"
                        onClick={() => togglePublishMutation.mutate()}
                        disabled={togglePublishMutation.isPending}
                        className="px-2.5 py-1 text-[11px] font-bold rounded bg-[#006838] hover:bg-[#00522c] text-white transition-colors"
                      >
                        Publish Store
                      </button>
                    )}
                  </div>
                </div>

                <div className="border-t border-[#E1E4E8] dark:border-slate-800 pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-[#202223] dark:text-slate-300">
                      Custom Primary Domain (Optional)
                    </label>
                    <span className="px-2 py-0.5 bg-[#E1E4E8] dark:bg-slate-800 text-[#5C5F62] text-[10px] font-semibold rounded uppercase">
                      DNS Config
                    </span>
                  </div>
                  <input
                    {...register('customDomain')}
                    disabled={!isEditMode}
                    placeholder="e.g. www.yourbrandstore.com"
                    className="w-full h-9 px-3 rounded-md border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                  />
                  <p className="text-[11px] text-[#5C5F62] dark:text-slate-400">
                    To link a custom domain, point your domain's CNAME record to <code className="bg-[#E1E4E8] dark:bg-slate-800 px-1 py-0.5 rounded text-[10px]">stores.dukene.com</code>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: SEO & SOCIAL MEDIA */}
        {activeTab === 'seo' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              {/* SEO Card */}
              <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 space-y-4">
                <div className="border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
                  <h3 className="text-sm font-semibold text-[#202223] dark:text-white">Search Engine Optimization (SEO)</h3>
                  <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                    Optimize search engine rankings and metadata snippet preview
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-[#202223] dark:text-slate-300 mb-1">
                      Meta Title
                    </label>
                    <input
                      {...register('metaTitle')}
                      disabled={!isEditMode}
                      placeholder="e.g. Dukene Store | Official Online Shop"
                      className="w-full h-9 px-3 rounded-md border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#202223] dark:text-slate-300 mb-1">
                      Meta Description
                    </label>
                    <textarea
                      {...register('metaDescription')}
                      disabled={!isEditMode}
                      rows={2}
                      placeholder="Compelling summary for Google search engines and social sharing cards..."
                      className="w-full p-3 rounded-md border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838] resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Social Channels with Authentic Brand Logos */}
              <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 space-y-4">
                <div className="border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
                  <h3 className="text-sm font-semibold text-[#202223] dark:text-white">Social Media Channels</h3>
                  <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                    Connect official social accounts displayed on your storefront header & footer icons
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2.5 p-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800">
                    <FacebookLogo className="w-5 h-5 flex-shrink-0" />
                    <input
                      {...register('facebook')}
                      disabled={!isEditMode}
                      placeholder="https://facebook.com/yourstore"
                      className="w-full text-xs bg-transparent border-none focus:outline-none text-[#202223] dark:text-white"
                    />
                  </div>

                  <div className="flex items-center gap-2.5 p-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800">
                    <InstagramLogo className="w-5 h-5 flex-shrink-0" />
                    <input
                      {...register('instagram')}
                      disabled={!isEditMode}
                      placeholder="https://instagram.com/yourstore"
                      className="w-full text-xs bg-transparent border-none focus:outline-none text-[#202223] dark:text-white"
                    />
                  </div>

                  <div className="flex items-center gap-2.5 p-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800">
                    <TikTokLogo className="w-5 h-5 flex-shrink-0 text-[#202223] dark:text-white" />
                    <input
                      {...register('tiktok')}
                      disabled={!isEditMode}
                      placeholder="https://tiktok.com/@yourstore"
                      className="w-full text-xs bg-transparent border-none focus:outline-none text-[#202223] dark:text-white"
                    />
                  </div>

                  <div className="flex items-center gap-2.5 p-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800">
                    <YouTubeLogo className="w-5 h-5 flex-shrink-0" />
                    <input
                      {...register('youtube')}
                      disabled={!isEditMode}
                      placeholder="https://youtube.com/yourstore"
                      className="w-full text-xs bg-transparent border-none focus:outline-none text-[#202223] dark:text-white"
                    />
                  </div>

                  <div className="flex items-center gap-2.5 p-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800">
                    <XLogo className="w-5 h-5 flex-shrink-0 text-[#202223] dark:text-white" />
                    <input
                      {...register('twitter')}
                      disabled={!isEditMode}
                      placeholder="https://x.com/yourstore"
                      className="w-full text-xs bg-transparent border-none focus:outline-none text-[#202223] dark:text-white"
                    />
                  </div>

                  <div className="flex items-center gap-2.5 p-2.5 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800">
                    <LinkedInLogo className="w-5 h-5 flex-shrink-0" />
                    <input
                      {...register('linkedin')}
                      disabled={!isEditMode}
                      placeholder="https://linkedin.com/company/yourstore"
                      className="w-full text-xs bg-transparent border-none focus:outline-none text-[#202223] dark:text-white"
                    />
                  </div>
                </div>
              </div>
              
              {/* Tracking Pixels */}
              <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 space-y-4">
                <div className="border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
                  <h3 className="text-sm font-semibold text-[#202223] dark:text-white">Tracking Pixels</h3>
                  <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                    Connect tracking IDs for analytics and retargeting
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#202223] dark:text-slate-300 mb-1">
                      Meta (Facebook) Pixel ID
                    </label>
                    <input
                      {...register('fbPixelId')}
                      disabled={!isEditMode}
                      placeholder="e.g. 123456789012345"
                      className="w-full h-9 px-3 rounded-md border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#202223] dark:text-slate-300 mb-1">
                      TikTok Pixel ID
                    </label>
                    <input
                      {...register('tiktokPixelId')}
                      disabled={!isEditMode}
                      placeholder="e.g. CD5X1..."
                      className="w-full h-9 px-3 rounded-md border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#202223] dark:text-slate-300 mb-1">
                      Google Tag Manager (GTM) Container ID
                    </label>
                    <input
                      {...register('gtmContainerId')}
                      disabled={!isEditMode}
                      placeholder="e.g. GTM-XXXXXXX"
                      className="w-full h-9 px-3 rounded-md border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-[#202223] dark:text-white focus:outline-none focus:ring-1 focus:ring-[#006838]"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Live Search Snippet Preview */}
            <div className="space-y-4">
              <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 space-y-3">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#5C5F62] uppercase tracking-wider">
                  <MagnifyingGlass weight="bold" className="w-3.5 h-3.5" />
                  Google Search Snippet Preview
                </div>
                <div className="p-3.5 rounded-lg bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 space-y-1">
                  <p className="text-[11px] text-[#5C5F62]">
                    https://{store?.subdomain || 'shop'}.dukene.com
                  </p>
                  <p className="text-sm font-medium text-blue-600 dark:text-blue-400 line-clamp-1 hover:underline cursor-pointer">
                    {watch('metaTitle') || store?.name || 'Your Online Store'}
                  </p>
                  <p className="text-xs text-[#5C5F62] dark:text-slate-400 line-clamp-3">
                    {watch('metaDescription') ||
                      'Explore our online storefront for high-quality products, verified reviews, and fast checkout.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: STOREFRONT FEATURES */}
        {activeTab === 'features' && (
          <div className="max-w-3xl space-y-4">
            <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-5 space-y-4">
              <div className="border-b border-[#E1E4E8] dark:border-slate-800 pb-3">
                <h3 className="text-sm font-semibold text-[#202223] dark:text-white">Customer Storefront Features</h3>
                <p className="text-xs text-[#5C5F62] dark:text-slate-400">
                  Toggle interactive customer experience modules on your online shop
                </p>
              </div>

              <div className="space-y-3">
                {[
                  {
                    id: 'enableReviews',
                    label: 'Verified Product Reviews & Star Ratings',
                    desc: 'Allow customers to submit and view product review scores on product details pages.',
                  },
                  {
                    id: 'enableWishlist',
                    label: 'Customer Wishlist & Saved Items',
                    desc: 'Let shoppers save products to a personal wishlist for future purchase.',
                  },
                  {
                    id: 'enableChat',
                    label: 'Live Support Chat Widget',
                    desc: 'Display a floating support chat button to assist shoppers in real-time.',
                  },
                ].map((feat) => {
                  const isChecked = watch(feat.id as any);
                  return (
                    <div
                      key={feat.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-[#E1E4E8] dark:border-slate-800 bg-[#F6F6F7] dark:bg-slate-900"
                    >
                      <div>
                        <p className="text-xs font-semibold text-[#202223] dark:text-white">{feat.label}</p>
                        <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">{feat.desc}</p>
                      </div>

                      <button
                        type="button"
                        onClick={() => isEditMode && setValue(feat.id as any, !isChecked, { shouldDirty: true })}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                          isChecked ? 'bg-[#006838]' : 'bg-[#8C9196]'
                        } ${!isEditMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            isChecked ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
